<div class="px-2 py-3">
  <!-- Header -->
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex items-start gap-3">
      <span class="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400">
        <lucide-icon [name]="History" class="h-5 w-5"></lucide-icon>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">Event History Lookup</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Enter up to {{ maxReferences }} event references to pull the full history for each.
        </p>
      </div>
    </div>
  </div>

  <!-- Lookup form -->
  <div class="mt-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
    <label for="references" class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
      Event references
    </label>
    <textarea
      id="references"
      rows="5"
      [(ngModel)]="rawReferences"
      (keydown.meta.enter)="submit()"
      (keydown.control.enter)="submit()"
      placeholder="One reference per line, or comma separated&#10;e.g.&#10;TVG-0001&#10;TVG-0002"
      class="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-gray-500"
    ></textarea>

    <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
      <div class="text-xs" [ngClass]="tooManyReferences ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'">
        <ng-container *ngIf="tooManyReferences; else countOk">
          {{ parsedReferences.length }} references entered — remove {{ parsedReferences.length - maxReferences }} to stay within the limit of {{ maxReferences }}.
        </ng-container>
        <ng-template #countOk>
          {{ parsedReferences.length }} / {{ maxReferences }} references
        </ng-template>
      </div>

      <button
        type="button"
        (click)="submit()"
        [disabled]="!canSubmit"
        class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <lucide-icon *ngIf="!loading" [name]="Search" class="h-4 w-4"></lucide-icon>
        <lucide-icon *ngIf="loading" [name]="Loader2" class="h-4 w-4 animate-spin"></lucide-icon>
        {{ loading ? 'Looking up…' : 'Look up history' }}
      </button>
    </div>
  </div>

  <!-- Error -->
  <div
    *ngIf="errorMessage"
    class="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50/60 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/[0.06] dark:text-red-300"
  >
    <lucide-icon [name]="TriangleAlert" class="mt-0.5 h-4 w-4"></lucide-icon>
    <span>{{ errorMessage }}</span>
  </div>

  <!-- References not found -->
  <div
    *ngIf="notFound.length"
    class="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-500/30 dark:bg-amber-500/[0.06]"
  >
    <div class="text-sm font-medium text-amber-700 dark:text-amber-300">
      No event found for: {{ notFound.join(', ') }}
    </div>
  </div>

  <!-- Empty state -->
  <div
    *ngIf="hasSearched && !loading && !histories.length && !notFound.length && !errorMessage"
    class="mt-4 rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400"
  >
    No events matched the references you entered.
  </div>

  <!-- Results toolbar -->
  <div *ngIf="histories.length" class="mt-4 flex flex-wrap items-center justify-between gap-3">
    <div class="text-sm text-gray-500 dark:text-gray-400">
      {{ histories.length }} {{ histories.length === 1 ? 'event' : 'events' }} found
    </div>
    <button
      type="button"
      (click)="exportCsv()"
      [disabled]="!canExport"
      class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-white/[0.03] dark:text-gray-200 dark:hover:bg-white/[0.06]"
    >
      <lucide-icon *ngIf="!exporting" [name]="Download" class="h-4 w-4"></lucide-icon>
      <lucide-icon *ngIf="exporting" [name]="Loader2" class="h-4 w-4 animate-spin"></lucide-icon>
      {{ exporting ? 'Exporting…' : 'Export CSV' }}
    </button>
  </div>

  <!-- Results -->
  <div class="mt-4 space-y-4">
    <div
      *ngFor="let event of histories"
      class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]"
    >
      <!-- Card header -->
      <div class="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 pb-3 dark:border-gray-800">
        <div>
          <div class="text-base font-semibold text-gray-800 dark:text-white/90">{{ event.reference }}</div>
          <div class="text-sm text-gray-500 dark:text-gray-400">{{ event.venue ?? '—' }}</div>
        </div>
        <span class="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-white/5 dark:text-white/70">
          {{ event.lifecycle_state ?? 'Unknown state' }}
        </span>
      </div>

      <!-- Detail grid -->
      <dl class="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Client</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">{{ event.client_name ?? '—' }}</dd>
          <dd class="text-xs text-gray-500 dark:text-gray-400">{{ event.client_email ?? '—' }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Event date</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">{{ event.event_date ?? '—' }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Show round</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">{{ event.show_round_date ?? '—' }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Guests (day / evening)</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">
            {{ event.day_guests ?? '—' }} / {{ event.evening_guests ?? '—' }}
          </dd>
        </div>

        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Menu</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">{{ event.menu.length ? event.menu.join('; ') : '—' }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Drinks package</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">
            {{ event.drinks_package.length ? event.drinks_package.join('; ') : '—' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Evening food</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">
            {{ event.evening_food.length ? event.evening_food.join('; ') : '—' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400">Extras</dt>
          <dd class="text-sm text-gray-800 dark:text-white/90">{{ event.extras.length ? event.extras.join('; ') : '—' }}</dd>
        </div>
      </dl>

      <!-- Financials -->
      <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.estimated_grand_total | currency: 'GBP' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Estimated grand total</div>
        </div>
        <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.minimum_spend | currency: 'GBP' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Minimum spend</div>
        </div>
        <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.minimum_spend_remaining | currency: 'GBP' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Still required</div>
        </div>
        <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.deposit | currency: 'GBP' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Deposit</div>
        </div>
      </div>

      <!-- Payments -->
      <div class="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <div class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.payments.total_due | currency: 'GBP' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Payments due</div>
        </div>
        <div class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.payments.total_paid | currency: 'GBP' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Payments paid</div>
        </div>
        <div class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.payments.total_outstanding | currency: 'GBP' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Outstanding</div>
        </div>
        <div class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ event.payments.next_due_date ?? '—' }}</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Next payment due</div>
        </div>
        <div class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="text-sm font-semibold text-gray-800 dark:text-white/90">
            {{ event.payments.next_due_amount != null ? (event.payments.next_due_amount | currency: 'GBP') : '—' }}
          </div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Next payment amount</div>
        </div>
      </div>

      <!-- Footer dates -->
      <div class="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
        <span>Created: {{ event.created_at ?? '—' }}</span>
        <span>Confirmed: {{ event.confirmed_at ?? '—' }}</span>
      </div>
    </div>
  </div>
</div>
